import { styled } from '@uhg-abyss/web/tools/styled';
import { Button as AbyssButton } from '@uhg-abyss/web/ui/Button';
import { isEmpty, isString, isUndefined, merge } from 'lodash';
import React, { CSSProperties, ReactNode } from 'react';

type ButtonVariant = 'destructive' | 'ghost' | 'outline' | 'solid';

export interface ButtonProps {
  children?: ReactNode;
  style?: CSSProperties;
  variant?: ButtonVariant;

  [key: string]: any;
}

/**
 * Button
 *
 * Still uses Abyss buttons, but overrides the default Abyss styling to better match UHG branding.
 */
export function Button({ children, style = {}, variant = 'solid', ...props }: ButtonProps) {
  const defaultProps: Partial<ButtonProps> = {
    children,
  };

  if (isUndefined(variant)) {
    defaultProps.variant = 'solid';
  }

  const newProps = { ...props, ...defaultProps, children, style, variant };

  let button: CSSProperties = merge(
    {},
    {
      border: 'unset !important',
      borderRadius: 'unset !important',
      borderStyle: 'solid !important',
      borderWidth: '2px !important',
      transition: 'all .33s ease-in-out !important',
    },
    style
  );

  let buttonHover: CSSProperties = {};
  let buttonText: CSSProperties = {};
  let buttonTextHover: CSSProperties = {};
  let icon: CSSProperties = {};
  let iconHover: CSSProperties = {};

  if (newProps?.variant === 'solid') {
    button = {
      ...button,
      backgroundColor: '#0066f5 !important',
      borderColor: '#0066f5 !important',
      color: '#ffffff !important',
    };

    buttonHover = {
      ...buttonHover,
      backgroundColor: '#ffffff !important',
      color: '#0066f5 !important',
    };

    buttonText = {
      ...buttonText,
      color: '#ffffff !important',
    };

    buttonTextHover = {
      ...buttonTextHover,
      color: '#0066f5 !important',
    };

    icon = {
      ...icon,
      color: '#ffffff !important',
    };

    iconHover = {
      ...iconHover,
      color: '#0066f5 !important',
    };
  }

  if (newProps?.variant === 'outline') {
    button = {
      ...button,
      backgroundColor: '#ffffff !important',
      borderColor: '#0066f5 !important',
      color: '#0066f5 !important',
    };

    buttonHover = {
      ...buttonHover,
      backgroundColor: '#0066f5 !important',
      color: '#ffffff !important',
    };

    buttonText = {
      ...buttonText,
      color: '#0066f5 !important',
    };

    buttonTextHover = {
      ...buttonTextHover,
      color: '#ffffff !important',
    };

    icon = {
      ...icon,
      color: '#0066f5 !important',
    };

    iconHover = {
      ...iconHover,
      color: '#ffffff !important',
    };
  }

  if (newProps?.variant === 'ghost') {
    button = {
      ...button,
      backgroundColor: 'transparent !important',
      borderColor: 'transparent !important',
      color: 'black !important',
      fontWeight: 'normal !important',
    };

    buttonHover = {
      ...buttonHover,
      backgroundColor: '#F3F3F3 !important',
      color: 'black !important',
    };

    buttonText = {
      ...buttonText,
      color: 'black !important !important',
    };

    buttonTextHover = {
      ...buttonTextHover,
      color: 'black !important !important',
    };

    icon = {
      ...icon,
      color: 'black !important',
    };

    iconHover = {
      ...iconHover,
      color: 'black !important',
    };
  }

  if (newProps?.variant === 'destructive') {
    button = {
      ...button,
      backgroundColor: 'var(--abyss-colors-button-color-background-root-destructive-default) !important',
      borderColor: 'var(--abyss-colors-button-color-border-root-destructive) !important',
      color: 'var(--abyss-colors-button-color-text-root-destructive-default) !important',
    };

    buttonHover = {
      ...buttonHover,
      backgroundColor: 'var(--abyss-colors-button-color-background-root-destructive-hover) !important',
      color: 'var(--abyss-colors-button-color-text-root-destructive-hover) !important',
    };

    buttonText = {
      ...buttonText,
      color: 'var(--abyss-colors-button-color-text-root-destructive-default) !important',
    };

    buttonTextHover = {
      ...buttonTextHover,
      color: 'var(--abyss-colors-button-color-text-root-destructive-hover) !important',
    };

    icon = {
      ...icon,
      color: 'var(--abyss-colors-button-color-text-root-destructive-default) !important',
    };

    iconHover = {
      ...iconHover,
      color: 'var(--abyss-colors-button-color-text-root-destructive-hover) !important',
    };
  }

  let Styles: React.ComponentType<any>;

  const disabledStyles: CSSProperties = {
    backgroundColor: 'var(--abyss-colors-button-color-background-root-isDisabled-true) !important',
    borderColor: 'var(--abyss-colors-button-color-border-root-isDisabled-true) !important',
  };

  if (!isString(children) || (isString(children) && isEmpty(children))) {
    disabledStyles.backgroundColor = 'transparent !important';
    disabledStyles.borderColor = 'transparent !important';
  }

  if (!['destructive', 'ghost', 'outline', 'solid'].includes(variant)) {
    Styles = styled('div', {
      'a, button': {
        '.abyss-insert-element-after': {
          paddingLeft: 'var(--abyss-space-xs) !important',
        },
        '.abyss-insert-element-before': {
          paddingRight: 'var(--abyss-space-xs) !important',
        },
      },
    });
  } else {
    Styles = styled('div', {
      'a, button': {
        ...button,
        '.abyss-icon': icon,
        '.abyss-insert-element-after': {
          paddingLeft: 'var(--abyss-space-xs) !important',
        },
        '.abyss-insert-element-before': {
          paddingRight: 'var(--abyss-space-xs) !important',
        },
        '&:hover': {
          ...buttonHover,
          '& .abyss-icon': iconHover,
          '& span': buttonTextHover,
        },
        '& span': buttonText,
      },
      'button[class*="isDisabled-true"]': {
        '& span, .abyss-icon': {
          color: 'var(--abyss-colors-button-color-text-root-isDisabled-true) !important',
        },
        ...disabledStyles,
        color: 'var(--abyss-colors-button-color-text-root-isDisabled-true) !important',
        cursor: 'not-allowed',
      },
    });
  }

  return (
    <Styles>
      <AbyssButton {...newProps}>{children}</AbyssButton>
    </Styles>
  );
}
